/**
 * A completed run as it was recorded: inputs, the solver chain, provenance, timings and mass balance
 * (GET /api/runs/{id}/record). The same sections for every solver, so the Run page reads the same at
 * every site; a section the solver recorded nothing for is not rendered.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'
import { DebrisQuantities, type DebrisCase } from './DebrisQuantities'
import type { Run } from '../api/types'

interface RecordRow {
  label: string
  value: string | number | boolean
  unit: string
  source: string | null
  group?: string
  duration_s?: number
}

interface RunRecordPayload {
  run_id: string
  solver: string
  site_id: string | null
  inputs?: RecordRow[]
  chain?: RecordRow[]
  provenance?: RecordRow[]
  timings?: RecordRow[]
  mass_balance?: RecordRow[]
  derived?: DebrisCase | null
}

function show(v: RecordRow['value']): string {
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v)
    if (Math.abs(v) >= 1e6 || (Math.abs(v) > 0 && Math.abs(v) < 1e-3)) return v.toExponential(3)
    return v.toLocaleString('en-IN', { maximumFractionDigits: 3 })
  }
  return String(v)
}

function Rows({ rows, testid }: { rows: RecordRow[]; testid: string }) {
  return (
    // table-fixed, because a source like "data/raw/dam2/DebrisLakeBreak.p01 (1).hdf: Results/Unsteady/
    // Summary/Volume Accounting" has nowhere to break and pushed the panel past the viewport at 1280.
    <table className="w-full table-fixed text-14 tabular-nums" data-testid={testid}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.label}-${i}`} className="border-t border-[var(--ink-border)] first:border-t-0">
            <td className="py-1.5 pr-4 align-top text-[var(--ink-text-dim)]">{r.label}</td>
            <td className="py-1.5 pr-4 text-right align-top font-mono">
              {show(r.value)}
              {r.unit ? ` ${r.unit}` : ''}
              {r.duration_s ? ` · ${show(r.duration_s)} s` : ''}
            </td>
            <td className="w-[44%] break-all py-1.5 align-top text-12 text-[var(--ink-text-faint)]">{r.source ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function RunRecord({ runs }: { runs: Run[] }) {
  const [runId, setRunId] = useState<string | null>(null)
  const current = runs.find((r) => r.id === runId) ?? runs[0] ?? null
  const record = useQuery<RunRecordPayload>({
    queryKey: ['run-record', current?.id],
    queryFn: () => api.raw(`/runs/${current!.id}/record`),
    enabled: Boolean(current),
    staleTime: Infinity,
  })
  const d = record.data
  const inputGroups = useMemo(() => {
    const groups = new Map<string, RecordRow[]>()
    for (const r of d?.inputs ?? []) {
      const key = r.group ?? 'Inputs'
      groups.set(key, [...(groups.get(key) ?? []), r])
    }
    return [...groups.entries()]
  }, [d])
  if (!current) return null
  return (
    <section className="panel" style={{ padding: 0 }} data-testid="run-record">
      <header className="panel-header">
        <h2 className="panel-title">5 · Recorded run</h2>
        {runs.length > 1 && (
          <select className="ctl h-10 !text-14" value={current.id} onChange={(e) => setRunId(e.target.value)} aria-label="Recorded run">
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.id} · {r.solver}
              </option>
            ))}
          </select>
        )}
      </header>
      {record.isPending && <div className="skeleton m-4 h-32" />}
      {d && (
        <div className="grid gap-6 p-4 xl:grid-cols-2">
          {inputGroups.length > 0 && (
            <div className="xl:col-span-2">
              <h3 className="section-label mb-2">Inputs</h3>
              <div className="grid gap-x-8 gap-y-4 xl:grid-cols-2">
                {inputGroups.map(([group, rows]) => (
                  <div key={group}>
                    {inputGroups.length > 1 && <div className="mb-1 text-14 font-semibold">{group}</div>}
                    <Rows rows={rows} testid="record-inputs" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.chain && (
            <div>
              <h3 className="section-label mb-2">Solver chain, as recorded</h3>
              <Rows rows={d.chain} testid="record-chain" />
            </div>
          )}
          {d.provenance && (
            <div>
              <h3 className="section-label mb-2">Provenance</h3>
              <Rows rows={d.provenance} testid="record-provenance" />
            </div>
          )}
          {d.timings && (
            <div>
              <h3 className="section-label mb-2">Timings</h3>
              <Rows rows={d.timings} testid="record-timings" />
            </div>
          )}
          {d.mass_balance && (
            <div>
              <h3 className="section-label mb-2">Mass balance, as the solver reported it</h3>
              <Rows rows={d.mass_balance} testid="record-mass" />
            </div>
          )}
          {d.derived && (
            <div data-testid="record-derived">
              <h3 className="section-label mb-2">Debris flow: the rheology this run was given, and what follows from it</h3>
              <DebrisQuantities block={d.derived} bare />
            </div>
          )}
        </div>
      )}
    </section>
  )
}
